import React, { useState, useCallback } from 'react';
import { View, Text, TextInput, TouchableOpacity, useWindowDimensions } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import PropTypes from 'prop-types';
import styles, { colors } from '../../style/rulesEngine';
import ConditionGroup from './ConditionGroup';
import ActionGroup from './ActionGroup';
import { validateRule } from './validation';
import RulesConfirmModal from './RulesConfirmModal';
import RuleValidationSummary from './RuleValidationSummary';
import {useNavigation} from '@react-navigation/native';
import UnsavedChangesPrompt from '../../commons/unsavedChanges/UnsavedChangesPrompt';
import useUnsavedChanges from '../../commons/unsavedChanges/useUnsavedChanges';

const RuleEditor = ({ rule, onSave, onCancel, saving, saveErrors }) => {
  const { width } = useWindowDimensions();
  const isNarrow = width < 600;
  const [name, setName] = useState(rule?.name || '');
  const [conditions, setConditions] = useState(
    rule?.conditions_json?.length > 0 ? rule.conditions_json : [{ field: null, operator: null, value: null }]
  );
  const [actions, setActions] = useState(
    rule?.actions_json?.length > 0
      ? rule.actions_json.map((a) => ({ type: a.type, value: a.params || a.value || {} }))
      : [{ type: null, value: {} }]
  );
  const [logic, setLogic] = useState(rule?.logic || 'all');
  const [errors, setErrors] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardPrompt, setShowDiscardPrompt] = useState(false);

  const markDirty = useCallback(() => {
    if (!hasUnsavedChanges) setHasUnsavedChanges(true);
  }, [hasUnsavedChanges]);

  // ─── Clear specific error keys ──────────────────────────────────────────

  const clearErrors = (...keys) => {
    setErrors((prev) => {
      const next = { ...prev };
      keys.forEach((k) => delete next[k]);
      return next;
    });
  };

  // ─── Condition handlers ─────────────────────────────────────────────────

  const handleConditionChange = (index, newCond) => {
    const updated = conditions.map((c, i) => (i === index ? newCond : c));
    setConditions(updated);
    markDirty();
    // Clear errors for whichever sub-fields now have a value
    const toClear = [];
    if (newCond.field) toClear.push(`condition_${index}_field`);
    if (newCond.operator) toClear.push(`condition_${index}_operator`);
    if (newCond.value !== '' && newCond.value !== null && newCond.value !== undefined) {
      toClear.push(`condition_${index}_value`);
    }
    if (toClear.length) clearErrors(...toClear);
  };

  const handleConditionRemove = (index) => {
    if (conditions.length <= 1) return;
    setConditions(conditions.filter((_, i) => i !== index));
    markDirty();
  };

  const handleConditionAdd = () => {
    setConditions([...conditions, { field: null, operator: null, value: null }]);
    markDirty();
  };

  // ─── Action handlers ────────────────────────────────────────────────────

  const handleActionChange = (index, newAction) => {
    const updated = actions.map((a, i) => (i === index ? newAction : a));
    setActions(updated);
    markDirty();
    // Clear errors for whichever sub-fields now have a value
    const toClear = [];
    if (newAction.type) toClear.push(`action_${index}_type`);
    if (newAction.value?.tag_id) toClear.push(`action_${index}_value`);
    if (newAction.value?.sku) toClear.push(`action_${index}_sku`);
    if (newAction.value?.quantity) toClear.push(`action_${index}_qty`);
    if (toClear.length) clearErrors(...toClear);
  };

  const handleActionRemove = (index) => {
    if (actions.length <= 1) return;
    setActions(actions.filter((_, i) => i !== index));
    markDirty();
  };

  const handleActionAdd = () => {
    setActions([...actions, { type: null, value: {} }]);
    markDirty();
  };

  // ─── Save ───────────────────────────────────────────────────────────────

  const navigation = useNavigation();
  const unsavedChanges = useUnsavedChanges({
    hasUnsavedChanges,
    onSave: () => handleSave(),
    onDiscard: () => setHasUnsavedChanges(false),
    navigation
  });

  const handleSave = () => {
    const validationErrors = validateRule({ name, conditions, actions });
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }

    onSave({
      id: rule?.id,
      name: name.trim(),
      conditions_json: conditions,
      actions_json: actions.map(({ type, value }) => ({ type, params: value || {} })),
      logic,
    });
  };

  // ─── Cancel ─────────────────────────────────────────────────────────────

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowDiscardPrompt(true);
    } else {
      onCancel();
    }
  };

  // ─── Merge client + server errors ───────────────────────────────────────

  const allErrors = { ...errors };
  if (saveErrors) {
    if (saveErrors.condition_cel) {
      allErrors.server_cel = Array.isArray(saveErrors.condition_cel)
        ? saveErrors.condition_cel.join('; ')
        : saveErrors.condition_cel;
    }
    Object.entries(saveErrors).forEach(([key, val]) => {
      if (key !== 'condition_cel') {
        allErrors[`server_${key}`] = Array.isArray(val) ? val.join('; ') : val;
      }
    });
  }

  return (
    <View style={styles.editorContainer}>
      {/* Validation summary — shown at top after failed save attempt */}
      <RuleValidationSummary errors={allErrors} />

      {/* Server CEL error */}
      {allErrors.server_cel && (
        <LinearGradient
          colors={['#CC0000', '#7D0000']}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={styles.errorBanner}
        >
          <Text style={styles.errorBannerText}>{allErrors.server_cel}</Text>
        </LinearGradient>
      )}

      {/* Rule name label */}
      <Text style={styles.sectionLabel}>Rule Name</Text>
      <TextInput
        style={[styles.nameInput, allErrors.name && styles.errorBorder]}
        value={name}
        onChangeText={(val) => { setName(val); markDirty(); if (val.trim()) clearErrors('name'); }}
        placeholder="Give this rule a name…"
        placeholderTextColor={colors.textMuted}
        maxLength={255}
      />
      {allErrors.name && <Text style={styles.errorText}>{allErrors.name}</Text>}

      {/* Conditions */}
      <ConditionGroup
        conditions={conditions}
        logicOperator={logic}
        onLogicChange={(val) => { setLogic(val); markDirty(); }}
        onConditionChange={handleConditionChange}
        onConditionRemove={handleConditionRemove}
        onConditionAdd={handleConditionAdd}
        errors={allErrors}
      />

      {/* Actions */}
      <ActionGroup
        actions={actions}
        onActionChange={handleActionChange}
        onActionRemove={handleActionRemove}
        onActionAdd={handleActionAdd}
        errors={allErrors}
      />

      {/* Footer */}
      {isNarrow ? (
        <View style={{ gap: 10, marginTop: 20, paddingTop: 16 }}>
          {hasUnsavedChanges && (
            <View style={{ alignItems: 'center' }}>
              <Text style={styles.unsavedBadgeText}>Unsaved changes</Text>
            </View>
          )}
          <TouchableOpacity
            testID="btn-save"
            onPress={handleSave}
            style={[styles.saveButton, { minWidth: 0, width: '100%' }, saving && { opacity: 0.6 }]}
            disabled={saving}
          >
            <Text style={styles.saveButtonText}>{saving ? 'Saving…' : 'Save Rule'}</Text>
          </TouchableOpacity>
          <TouchableOpacity testID="btn-cancel" onPress={handleCancel} style={[styles.cancelButton, { minWidth: 0, width: '100%' }]}>
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <View style={styles.editorFooter}>
          <TouchableOpacity testID="btn-cancel" onPress={handleCancel} style={styles.cancelButton}>
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
          {hasUnsavedChanges && (
            <View style={styles.unsavedBadge}>
              <Text style={styles.unsavedBadgeText}>Unsaved changes</Text>
            </View>
          )}
          <TouchableOpacity
            testID="btn-save"
            onPress={handleSave}
            style={[styles.saveButton, saving && { opacity: 0.6 }]}
            disabled={saving}
          >
            <Text style={styles.saveButtonText}>{saving ? 'Saving…' : 'Save Rule'}</Text>
          </TouchableOpacity>
        </View>
      )}

      <UnsavedChangesPrompt
        visible={unsavedChanges.promptVisible}
        saving={unsavedChanges.saving}
        onSave={unsavedChanges.handleSave}
        onDiscard={unsavedChanges.handleDiscard}
        onStay={unsavedChanges.handleStay}
      />

      {/* Discard changes confirmation */}
      <RulesConfirmModal
        visible={showDiscardPrompt}
        title="Discard changes?"
        message="You have unsaved changes. Are you sure you want to discard them?"
        confirmText="Discard"
        cancelText="Keep editing"
        onConfirm={() => { setShowDiscardPrompt(false); onCancel(); }}
        onCancel={() => setShowDiscardPrompt(false)}
      />
    </View>
  );
};

RuleEditor.propTypes = {
  rule: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  saveErrors: PropTypes.object,
};

export default RuleEditor;
